import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiLogOut, FiMenu, FiX, FiTrash2 } from "react-icons/fi";
import { RiContactsBookLine } from "react-icons/ri";
import { FaGlobe } from "react-icons/fa";
import '../styles/Chat.css';
import '../styles/LanguageSelector.css';
import ChatBox from '../components/ChatBox';
import FeedbackForm from '../components/FeedbackForm';
import ProfileWidget from '../components/ProfileWidget';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Home = () => {
    const navigate = useNavigate();
    const [chatHistory, setChatHistory] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const [showLanguageModal, setShowLanguageModal] = useState(false);

    useEffect(() => {
        fetchChatHistory();
    }, []);

    useEffect(() => {
        const storedChatId = localStorage.getItem('selectedChatId');
        if (storedChatId && chatHistory.length > 0) {
            const chat = chatHistory.find(chat => chat._id === storedChatId);
            if (chat) {
                setSelectedChat(chat);
            }
        } else if (chatHistory.length === 0) {
            setShowLanguageModal(true);
        }
    }, [chatHistory]);

    const fetchChatHistory = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_APP_URL}/api/chats/history`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            setChatHistory(res.data);
        } catch (error) {
            console.error('Error fetching chat history:', error);
        }
    };

    const handleChatSelect = async (chat) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_APP_URL}/api/chats/${chat._id}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            setSelectedChat(response.data);
            localStorage.setItem('selectedChatId', chat._id);
            setSidebarOpen(false);
        } catch (error) {
            console.error('Error fetching chat data:', error);
        }
    };

    const handleChatDelete = async (e, chatId) => {
        e.stopPropagation();
        
        if (selectedChat && selectedChat._id === chatId) {
            setSelectedChat(null);
            localStorage.removeItem('selectedChatId');
        }
        
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_APP_URL}/api/chats/delete/${chatId}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            
            setChatHistory(prevHistory => prevHistory.filter(chat => chat._id !== chatId));
            toast.success('Chat deleted successfully');
        } catch (error) {
            console.error('Error deleting chat:', error);
            toast.error('Error deleting chat');
        }
    };

    const handleFeedbackSubmit = async (formData) => {
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_APP_URL}/api/feedback`, formData, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            toast.success('Feedback submitted successfully');
        } catch (error) {
            console.error('Error submitting feedback:', error);
            toast.error('Error submitting feedback');
        }
    };

    const handleLogout = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_APP_URL}/api/chats/history`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const chatToDelete = res.data.find(chat => chat.messages.length === 1);

            if (chatToDelete) {
                await axios.delete(`${process.env.REACT_APP_BACKEND_APP_URL}/api/chats/delete/${chatToDelete._id}`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
            }

            localStorage.removeItem('token');
            localStorage.removeItem('selectedChatId');
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith('chat-language-')) {
                    localStorage.removeItem(key);
                }
            });
            
            navigate('/');
        } catch (error) {
            console.error('Error during logout:', error);
        }
    };

    const getWelcomeMessage = (language) => {
        if (language === 'sinhala') {
            return "ශ්‍රී ලංකා ගිණුම්කරණ ප්‍රමිතිය සඳහා වන IFRS දැනුම් Bot වෙත ඔබව සාදරයෙන් පිළිගනිමු! ඔබ ගිණුම්කරණ විද්‍යාවේ සංකීර්ණ ප්‍රමිතීන් සමග කටයුතු කරන ශිෂ්‍යයෙකු හෝ නිවැරදි තොරතුරු මූලාශ්‍රයන් සොයන ගුරුවරයෙකු වුවද, ගිණුම්කරණ විද්‍යාවේ දැනුම සරල ආකාරයකින් හඳුනාගැනීමේ සහයට මා මෙහි සිටිමි. අපි එකට LKAS ගවේෂණය කරමු! 💡";
        }
        return "Welcome to the IFRS Knowledge Bot Your intelligent companion for Sri Lanka Accounting Standards. Whether you're a student navigating complex standards or a teacher seeking precise references, I'm here to simplify accounting knowledge. Let's explore LKAS together! 💡";
    };

    const handleNewChat = () => {
        setShowLanguageModal(true);
    };

    const createNewChat = async (language) => {
        const user = JSON.parse(localStorage.getItem("user"));
        const userId = user ? user.id : null;

        if (!userId) {
            console.error("User ID is not available. Cannot create new chat.");
            return;
        }

        if (chatHistory.length >= 8) {
            toast.error(
                "Your chat threads have reached the maximum limit. Please delete any unwanted chat threads from your history."
            );
            return;
        }

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_APP_URL}/api/users/new-chat`,
                {
                    title: `Chat Thread - ${new Date().toLocaleString()}`,
                    userId: userId,
                    initialMessage: getWelcomeMessage(language),
                    role: "assistant",
                    language: language
                },
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            setSelectedChat(res.data);
            localStorage.setItem("selectedChatId", res.data._id);
            fetchChatHistory();
            setSidebarOpen(false);
            setShowLanguageModal(false);
        } catch (error) {
            console.error('Error creating new chat:', error);
            toast.error('Error creating new chat');
        }
    };

    const handleNewMessage = async () => {
        await fetchChatHistory();
    };

    const LanguageDisplay = ({ language }) => {
        const languages = [
            { code: 'english', name: 'English', flag: '🇺🇸' },
            { code: 'sinhala', name: 'සිංහල', flag: '🇱🇰' }
        ];
        const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

        return (
            <div className="language-display">
                <FaGlobe className="language-icon" />
                <span className="language-flag">{currentLanguage.flag}</span>
                <span className="language-name">{currentLanguage.name}</span>
            </div>
        );
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    return (
        <div className="chat-container">
            <button className="mobile-menu-button" onClick={toggleSidebar}>
                {sidebarOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>

            <div 
                className={`mobile-overlay ${sidebarOpen ? 'active' : ''}`} 
                onClick={() => setSidebarOpen(false)}
            />
            
            <div className={`sidebar ${sidebarOpen ? 'active' : ''}`}>
                <div className="logo-container">
                    <img
                        src="/logo.png"
                        alt="Logo"
                        className="sidebar-logo"
                        onClick={() => navigate('/')}
                    /> 
                </div>
                <button className="new-chat-button" onClick={handleNewChat}>
                    <span>+</span> New Chat 
                </button>
                <div className="chat-history">
                    {chatHistory.map((chat) => (
                        <div
                            key={chat._id}
                            className={`chat-history-item ${selectedChat?._id === chat._id ? 'active' : ''}`}
                        >
                            <div className="chat-item-content">
                                <div 
                                    className="chat-history-title"
                                    onClick={() => handleChatSelect(chat)}
                                >
                                    <span>{chat.title}</span> 
                                </div>
                                <button 
                                    className="chat-history-delete" 
                                    onClick={(e) => handleChatDelete(e, chat._id)}
                                    aria-label="Delete chat"
                                >
                                    <FiTrash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="sidebar-footer">
                    <button
                        className="faq-button sidebar-footer-item"
                        onClick={() => setIsFeedbackOpen(true)}
                    >
                        <RiContactsBookLine size={18} />
                        <span>Give us your feedback</span>
                    </button>

                    <FeedbackForm
                        isOpen={isFeedbackOpen}
                        onClose={() => setIsFeedbackOpen(false)}
                        onSubmit={handleFeedbackSubmit}
                    />
                    <button
                        className="logout-button sidebar-footer-item"
                        onClick={handleLogout}
                    >
                        <FiLogOut size={18} />
                        <span>Log out</span>
                    </button>
                </div>
            </div>
            <div className="main-content">
                <nav className="nav-container">
                    <ProfileWidget />
                    {selectedChat && (
                        <LanguageDisplay language={selectedChat.language} />
                    )}
                </nav>
                {selectedChat && (
                    <ChatBox 
                        selectedChat={selectedChat} 
                        onNewMessage={handleNewMessage}
                    />
                )}
            </div>

            {showLanguageModal && (
                <div className="modal-overlay" onClick={() => setShowLanguageModal(false)}>
                <div className="modal" onClick={(e) => e.stopPropagation()}>
                    <h2>Select Language for New Chat</h2>
                    <div className="modal-buttons">
                    <button className="modal-button" onClick={() => createNewChat('english')}>
                        English 🇺🇸
                    </button>
                    <button className="modal-button" onClick={() => createNewChat('sinhala')}>
                        සිංහල 🇱🇰
                    </button>
                    </div>
                </div>
                </div>
                            )}
        </div>
    );
};

export default Home;